/**
 * 日付フォーマット関連のユーティリティ関数
 * 複数のコンポーネントで重複していた日付処理を統合
 */


/**
 * 様々な日付形式を安全にYYYY/MM/DD形式でフォーマットする
 * @param dateValue - Date, Firestore Timestamp, 文字列, またはその他の日付値
 * @returns フォーマットされた日付文字列（YYYY/MM/DD）、無効な場合は空文字
 */
export const safeFormatDate = (dateValue: any): string => {
  try {
    let date: Date;
    
    // Firestore Timestamp の場合
    if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
      date = (dateValue as any).toDate();
    }
    // Date オブジェクトの場合
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    // 文字列の場合（EXIF形式: "YYYY:MM:DD HH:MM:SS"）
    else if (typeof dateValue === 'string') {
      const dateStr = dateValue.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      date = new Date(dateStr);
    }
    // その他の場合
    else {
      date = new Date(dateValue);
    }
    
    // 有効な日付かチェック
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  } catch (error) {
    return '';
  }
};

/**
 * 基本的な日付フォーマット（CharacterCard等で使用）
 * @param date - Date オブジェクト
 * @returns フォーマットされた日付文字列（YYYY/MM/DD）
 */
export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

/**
 * 詳細な日時フォーマット（ログや詳細表示用）
 * @param dateValue - 様々な形式の日付値
 * @returns フォーマットされた日時文字列（YYYY/MM/DD HH:MM:SS）、無効な場合は空文字
 */
export const formatDateTime = (dateValue: any): string => {
  try {
    let date: Date;
    
    if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
      date = (dateValue as any).toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      const dateStr = dateValue.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      date = new Date(dateStr);
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return '';
  }
};

/**
 * 相対時間フォーマット（例: "2 days ago"）
 * @param dateValue - 様々な形式の日付値
 * @returns 相対時間文字列、無効な場合は空文字
 */
export const formatRelativeTime = (dateValue: any): string => {
  try {
    let date: Date;
    
    if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
      date = (dateValue as any).toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) {
      return '';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    return '';
  }
};